import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, Warning, Buildings, Users, CheckCircle, Trash,
  Plus, Eye, X, CircleNotch, EnvelopeSimple, Lock, UserMinus,
  FileArrowDown, ClockCounterClockwise, Prohibit, MagnifyingGlass
} from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompliancePage() {
  const [overview, setOverview] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('');
  // GDPR
  const [gdprStats, setGdprStats] = useState(null);
  const [suppList, setSuppList] = useState([]);
  const [suppTotal, setSuppTotal] = useState(0);
  const [consentLog, setConsentLog] = useState([]);
  const [consentTotal, setConsentTotal] = useState(0);
  const [newSuppEmail, setNewSuppEmail] = useState('');
  const [newSuppReason, setNewSuppReason] = useState('manual');
  const [addingSup, setAddingSup] = useState(false);
  const [showAddSup, setShowAddSup] = useState(false);
  // Contact lookup
  const [lookupId, setLookupId] = useState('');
  const [contactData, setContactData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const params = country ? `?country=${country}` : '';
    Promise.all([
      fetch(`${API}/compliance${params}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/compliance/flags`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([o, f]) => { setOverview(o); setFlags(f); }).finally(() => setLoading(false));
  }, [country]);

  const fetchGdprStats = useCallback(() => {
    fetch(`${API}/gdpr/stats`, { credentials: 'include' }).then(r => r.json()).then(setGdprStats).catch(() => {});
  }, []);

  const fetchSuppression = useCallback(() => {
    fetch(`${API}/gdpr/suppression?limit=100`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setSuppList(d.items || []); setSuppTotal(d.total || 0); }).catch(() => {});
  }, []);

  const fetchConsentLog = useCallback(() => {
    fetch(`${API}/gdpr/consent-log?limit=50`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setConsentLog(d.logs || []); setConsentTotal(d.total || 0); }).catch(() => {});
  }, []);

  useEffect(() => { fetchGdprStats(); fetchSuppression(); fetchConsentLog(); }, [fetchGdprStats, fetchSuppression, fetchConsentLog]);

  const addSuppression = async () => {
    if (!newSuppEmail) return;
    setAddingSup(true);
    await fetch(`${API}/gdpr/suppression`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: newSuppEmail, reason: newSuppReason }),
    });
    setNewSuppEmail(''); setShowAddSup(false);
    fetchSuppression(); fetchGdprStats();
    setAddingSup(false);
  };

  const removeSuppression = async (id) => {
    await fetch(`${API}/gdpr/suppression/${id}`, { method: 'DELETE', credentials: 'include' });
    fetchSuppression(); fetchGdprStats();
  };

  const lookupContact = async () => {
    if (!lookupId) return;
    setLookupLoading(true); setContactData(null);
    try {
      const res = await fetch(`${API}/gdpr/contact/${lookupId}/export`, { credentials: 'include' });
      if (res.ok) setContactData(await res.json());
      else setContactData({ error: 'Contact niet gevonden' });
    } catch { setContactData({ error: 'Fout bij ophalen' }); }
    setLookupLoading(false);
  };

  const deleteContact = async (contactId) => {
    setDeleting(true);
    await fetch(`${API}/gdpr/contact/${contactId}/delete`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    setContactData(null); setDeleteConfirm('');
    fetchGdprStats(); fetchSuppression(); fetchConsentLog();
    setDeleting(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="compliance-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Compliance & GDPR</h1>
          <p className="text-sm text-zinc-500 mt-1">AVG/GDPR naleving, suppression list, consent logging en rechten van betrokkenen</p>
        </div>
      </div>

      <Tabs defaultValue="gdpr">
        <TabsList className="w-full justify-start border-b border-zinc-200 rounded-none bg-transparent p-0 h-auto mb-6">
          {['gdpr', 'suppression', 'consent-log', 'rights', 'data-quality'].map(tab => (
            <TabsTrigger key={tab} value={tab} data-testid={`tab-${tab}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F172A] data-[state=active]:font-semibold data-[state=active]:shadow-none px-5 py-3 text-sm">
              {tab === 'gdpr' ? 'GDPR Overzicht' : tab === 'suppression' ? 'Suppression List' : tab === 'consent-log' ? 'Consent Log' : tab === 'rights' ? 'Rechten' : 'Data Kwaliteit'}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* GDPR OVERVIEW */}
        <TabsContent value="gdpr" className="mt-0">
          {gdprStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="gdpr-stats">
              {[
                { l: 'Contacten', v: gdprStats.total_contacts, c: 'text-zinc-700' },
                { l: 'Gesuppressed', v: gdprStats.suppressed, c: gdprStats.suppressed > 0 ? 'text-red-500' : 'text-zinc-400' },
                { l: 'Afgemeld', v: gdprStats.unsubscribed, c: gdprStats.unsubscribed > 0 ? 'text-amber-600' : 'text-zinc-400' },
                { l: 'Consent Events', v: gdprStats.consent_events, c: 'text-blue-600' },
                { l: 'Verwijderingen', v: gdprStats.deletions_processed, c: 'text-purple-600' },
                { l: 'Inactief (12m+)', v: gdprStats.stale_contacts, c: gdprStats.stale_contacts > 0 ? 'text-amber-600' : 'text-zinc-400' },
                { l: 'Geen activiteit', v: gdprStats.no_activity_contacts, c: 'text-zinc-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white p-3 text-center">
                  <div className={`font-data text-xl font-bold ${s.c}`}>{s.v}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Email policy info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="border border-emerald-200 bg-emerald-50/50 p-5 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={18} weight="bold" className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-emerald-900">Email 1: Gerechtvaardigd Belang</h3>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed">
                De eerste email in elke sequence wordt verstuurd op basis van <strong>Art. 6.1.f AVG</strong> (gerechtvaardigd belang).
                Er is <strong>geen afmeldlink</strong> in deze email — alleen bedrijfsgegevens en AVG-vermelding.
                Dit is toegestaan voor B2B first contact in de EU.
              </p>
            </div>
            <div className="border border-blue-200 bg-blue-50/50 p-5 rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <EnvelopeSimple size={18} weight="bold" className="text-blue-600" />
                <h3 className="text-sm font-semibold text-blue-900">Email 2-6: Met Afmeldoptie</h3>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed">
                Alle opvolgende emails bevatten een <strong>afmeldlink</strong> en link naar <strong>gegevensinzage</strong>.
                Afmeldingen worden direct verwerkt: contact op suppression list, campagne gestopt,
                alle geplande emails geannuleerd.
              </p>
            </div>
          </div>

          {/* Legal basis summary */}
          <div className="border border-zinc-200 bg-white p-5 rounded-sm">
            <h3 className="text-sm font-semibold mb-3">Verwerkingsgrondslagen</h3>
            <div className="space-y-2 text-xs">
              {[
                { basis: 'Gerechtvaardigd belang (Art. 6.1.f)', desc: 'Eerste zakelijk contact met relevante organisaties in de zwembadsector', status: 'Actief' },
                { basis: 'Toestemming (Art. 6.1.a)', desc: 'Expliciete opt-in bij inschrijving nieuwsbrief of via website', status: 'Beschikbaar' },
                { basis: 'Recht op bezwaar (Art. 21)', desc: 'Contacten kunnen bezwaar maken via afmeldlink of privacy@poolfix.nl', status: 'Actief' },
                { basis: 'Recht op verwijdering (Art. 17)', desc: 'Alle data wordt verwijderd op verzoek, met audit trail', status: 'Actief' },
                { basis: 'Recht op inzage (Art. 15)', desc: 'Contacten kunnen hun gegevens opvragen via privacy@poolfix.nl', status: 'Actief' },
              ].map((b, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-zinc-50 rounded-sm">
                  <div>
                    <span className="font-medium text-zinc-800">{b.basis}</span>
                    <p className="text-zinc-500 mt-0.5">{b.desc}</p>
                  </div>
                  <Badge className="rounded-sm px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-700">{b.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* SUPPRESSION LIST */}
        <TabsContent value="suppression" className="mt-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Prohibit size={18} weight="bold" className="text-red-500" />
              <h3 className="font-heading text-base font-semibold">Suppression List</h3>
              <Badge className="rounded-full px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600">{suppTotal}</Badge>
            </div>
            <button data-testid="add-suppression-btn" onClick={() => setShowAddSup(!showAddSup)}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors">
              <Plus size={12} /> Toevoegen
            </button>
          </div>

          {showAddSup && (
            <div className="border border-red-200 bg-red-50 p-4 mb-4 rounded-sm" data-testid="add-suppression-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Email</label>
                  <Input data-testid="supp-email" value={newSuppEmail} onChange={e => setNewSuppEmail(e.target.value)} placeholder="email@voorbeeld.nl" className="rounded-sm text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500 mb-1 block">Reden</label>
                  <Select value={newSuppReason} onValueChange={setNewSuppReason}>
                    <SelectTrigger className="rounded-sm text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Handmatig toegevoegd</SelectItem>
                      <SelectItem value="bounce">Bounce / ongeldig</SelectItem>
                      <SelectItem value="complaint">Klacht ontvangen</SelectItem>
                      <SelectItem value="gdpr_request">GDPR verzoek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <button onClick={addSuppression} disabled={addingSup || !newSuppEmail} data-testid="confirm-add-supp"
                    className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors">
                    {addingSup ? 'Toevoegen...' : 'Toevoegen'}
                  </button>
                  <button onClick={() => setShowAddSup(false)} className="px-4 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm">Annuleren</button>
                </div>
              </div>
            </div>
          )}

          <div className="border border-zinc-200 overflow-hidden rounded-sm" data-testid="suppression-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Email</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Reden</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Bron</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Actie</th>
                </tr>
              </thead>
              <tbody>
                {suppList.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 text-xs">
                    <ShieldCheck size={24} className="mx-auto mb-2 text-emerald-400" />
                    Suppression list is leeg.
                  </td></tr>
                ) : suppList.map(s => (
                  <tr key={s.suppression_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-sm font-medium">{s.email}</td>
                    <td className="px-4 py-2.5">
                      <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${
                        s.reason === 'unsubscribe' ? 'bg-amber-100 text-amber-700' :
                        s.reason === 'gdpr_deletion' ? 'bg-red-100 text-red-700' :
                        s.reason === 'bounce' ? 'bg-purple-100 text-purple-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>{s.reason}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">{s.source || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">{s.suppressed_at ? new Date(s.suppressed_at).toLocaleDateString('nl-NL') : '-'}</td>
                    <td className="px-4 py-2.5 text-right">
                      {!s.deletion_requested && (
                        <button onClick={() => removeSuppression(s.suppression_id)} className="p-1 hover:bg-red-50 rounded-sm" title="Verwijderen">
                          <Trash size={14} className="text-zinc-400 hover:text-red-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CONSENT LOG */}
        <TabsContent value="consent-log" className="mt-0">
          <div className="flex items-center gap-2 mb-4">
            <ClockCounterClockwise size={18} weight="bold" className="text-[#0055FF]" />
            <h3 className="font-heading text-base font-semibold">Consent Audit Log</h3>
            <Badge className="rounded-full px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600">{consentTotal}</Badge>
          </div>

          <div className="border border-zinc-200 overflow-hidden rounded-sm" data-testid="consent-log-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Contact</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Grondslag</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Actie</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Campagne</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-widest text-zinc-500">Datum</th>
                </tr>
              </thead>
              <tbody>
                {consentLog.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400 text-xs">Nog geen consent events gelogd.</td></tr>
                ) : consentLog.map((l, i) => (
                  <tr key={l.log_id || i} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-2.5">
                      <div className="text-sm">{l.email || '-'}</div>
                      <div className="text-[10px] text-zinc-400">{l.contact_id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={`rounded-sm px-2 py-0.5 text-[10px] ${
                        l.legal_basis === 'legitimate_interest' ? 'bg-blue-100 text-blue-700' :
                        l.legal_basis === 'consent_withdrawn' ? 'bg-red-100 text-red-700' :
                        l.legal_basis === 'gdpr_right_to_erasure' ? 'bg-purple-100 text-purple-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>{l.legal_basis?.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-zinc-600">{l.action?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-400 font-data">{l.campaign_id || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-zinc-500">{l.timestamp ? new Date(l.timestamp).toLocaleString('nl-NL') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* RIGHTS (Data subject rights) */}
        <TabsContent value="rights" className="mt-0">
          <div className="border border-zinc-200 bg-white p-5 mb-5 rounded-sm">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={18} weight="bold" className="text-[#0055FF]" />
              <h3 className="font-heading text-base font-semibold">Rechten van Betrokkenen</h3>
            </div>
            <p className="text-sm text-zinc-500 mb-4">Zoek een contact op ID om gegevens in te zien, te exporteren of te verwijderen.</p>

            <div className="flex gap-3 mb-4">
              <Input data-testid="lookup-contact-id" value={lookupId} onChange={e => setLookupId(e.target.value)}
                placeholder="Contact ID (bijv. con_abc123)" className="rounded-sm text-sm max-w-sm" />
              <button data-testid="lookup-btn" onClick={lookupContact} disabled={lookupLoading || !lookupId}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {lookupLoading ? <CircleNotch size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
                Opzoeken
              </button>
            </div>

            {contactData && !contactData.error && (
              <div className="space-y-4" data-testid="contact-data-view">
                {/* Contact info */}
                <div className="border border-zinc-200 p-4 rounded-sm">
                  <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Contactgegevens</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {[
                      { l: 'Naam', v: contactData.contact?.full_name },
                      { l: 'Email', v: contactData.contact?.email },
                      { l: 'Organisatie', v: contactData.contact?.organization },
                      { l: 'Functie', v: contactData.contact?.role_title },
                      { l: 'Telefoon', v: contactData.contact?.phone },
                      { l: 'Land', v: contactData.contact?.country_code },
                      { l: 'Afgemeld', v: contactData.contact?.email_marketing?.unsubscribed ? 'Ja' : 'Nee' },
                      { l: 'Emails ontvangen', v: contactData.contact?.email_marketing?.total_emails_sent || 0 },
                    ].map((f, i) => (
                      <div key={i}>
                        <span className="text-xs text-zinc-400">{f.l}</span>
                        <div className="font-medium">{f.v || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="border border-zinc-200 p-3 rounded-sm">
                    <div className="font-data text-lg font-bold text-zinc-700">{contactData.email_events?.length || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Email events</div>
                  </div>
                  <div className="border border-zinc-200 p-3 rounded-sm">
                    <div className="font-data text-lg font-bold text-zinc-700">{contactData.replies?.length || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Replies</div>
                  </div>
                  <div className="border border-zinc-200 p-3 rounded-sm">
                    <div className="font-data text-lg font-bold text-zinc-700">{contactData.consent_log?.length || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Consent events</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2 border-t border-zinc-200">
                  <a href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(contactData, null, 2))}`}
                    download={`gdpr_export_${contactData.contact?.contact_id}.json`}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0055FF] text-white rounded-sm hover:bg-blue-700 transition-colors" data-testid="export-contact-btn">
                    <FileArrowDown size={14} /> Exporteer Gegevens (JSON)
                  </a>
                  {deleteConfirm === contactData.contact?.contact_id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-600 font-medium">Weet u het zeker? Dit is permanent.</span>
                      <button data-testid="confirm-delete-btn" onClick={() => deleteContact(contactData.contact.contact_id)} disabled={deleting}
                        className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors">
                        {deleting ? 'Verwijderen...' : 'Ja, verwijder alles'}
                      </button>
                      <button onClick={() => setDeleteConfirm('')} className="px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm">Annuleren</button>
                    </div>
                  ) : (
                    <button data-testid="delete-contact-btn" onClick={() => setDeleteConfirm(contactData.contact?.contact_id)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white text-red-600 border border-red-200 rounded-sm hover:bg-red-50 transition-colors">
                      <UserMinus size={14} /> Verwijder Alle Gegevens (Art. 17)
                    </button>
                  )}
                </div>
              </div>
            )}

            {contactData?.error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-600" data-testid="lookup-error">
                <Warning size={14} className="inline mr-1" /> {contactData.error}
              </div>
            )}
          </div>
        </TabsContent>

        {/* DATA QUALITY */}
        <TabsContent value="data-quality" className="mt-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-base font-semibold">Data Kwaliteit</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Compliance flags en datakwaliteit per land</p>
            </div>
            <Select value={country} onValueChange={v => setCountry(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-48 rounded-sm text-sm"><SelectValue placeholder="Alle landen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle landen</SelectItem>
                <SelectItem value="NL">Nederland</SelectItem><SelectItem value="BE">Belgie</SelectItem>
                <SelectItem value="DE">Duitsland</SelectItem><SelectItem value="FR">Frankrijk</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="data-quality-stats">
              {[
                { l: 'Faciliteiten', v: overview.total_facilities, c: 'text-zinc-600', i: Buildings },
                { l: 'Geverifieerd', v: overview.verified, c: 'text-emerald-600', i: CheckCircle },
                { l: 'Export-klaar', v: overview.compliant, c: 'text-[#0055FF]', i: ShieldCheck },
                { l: 'Geflagged', v: overview.flagged, c: overview.flagged > 0 ? 'text-amber-600' : 'text-zinc-400', i: Warning },
              ].map((s, i) => (
                <div key={i} className="bg-white p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <s.i size={16} weight="bold" className={s.c} />
                    <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{s.l}</span>
                  </div>
                  <div className="font-data text-2xl font-bold">{s.v}</div>
                </div>
              ))}
            </div>
          )}

          <div className="border border-zinc-200 bg-white rounded-sm" data-testid="compliance-flags-list">
            <div className="p-4 border-b border-zinc-200">
              <h3 className="text-sm font-semibold">Compliance Flags</h3>
            </div>
            {flags.length === 0 ? (
              <div className="p-12 text-center">
                <ShieldCheck size={32} className="text-emerald-500 mx-auto mb-2" />
                <span className="text-emerald-600 text-sm font-medium">Alle records zijn compliant</span>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {flags.map(f => (
                  <div key={f.flag_id} className="flex items-center gap-4 px-5 py-4 hover:bg-zinc-50 transition-colors">
                    <Warning size={18} className={f.severity === 'error' ? 'text-red-500' : 'text-amber-500'} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{f.flag_type?.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-zinc-500">{f.message}</div>
                    </div>
                    <Badge className={`rounded-sm px-2 py-0.5 text-xs ${f.severity === 'error' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{f.severity}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
